import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const skills = [
    'Frontend Development',
    'Backend Development',
    'UI/UX Design',
  ];

  return (
    <section id="about" className="py-32 relative">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="max-w-6xl mx-auto"
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">About </span>
              <span className="text-gradient">Me</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-white to-gray-400 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={itemVariants} className="space-y-6">
              <p className="text-lg text-gray-400 leading-relaxed">
              Hi 👋, I'm Raphael Okeke. A full-stack developer and creative technologist passionate about building beautiful, interactive digital experiences.
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
              With a background in design and development, I bridge the gap between aesthetics and functionality. I love exploring new technologies and bringing innovative ideas to life.

My expertise spans:

Modern web development with React, Next.js, and TypeScript,
Full-stack architecture and database design,
Interactive UI/UX design and implementation,
Performance optimization and accessibility
Creative experiments and unique digital concepts.
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
              When I'm not coding, you can find me exploring design trends, experimenting with new tools, or working on passion projects. I believe in continuous learning and pushing the boundaries of what's possible on the web.

Let's build something amazing together. Feel free to explore this portfolio and reach out if you'd like to collaborate.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-6">
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="flex justify-center mb-10"
              >
                <div className="relative w-60 h-60 md:w-72 md:h-72">
                  <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="w-full h-full rounded-full overflow-hidden border-4 border-white/30 shadow-2xl"
                  >
                    <img
                      src="/pfp.jpg"
                      alt="Raphael Okeke"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.style.backgroundColor = '#1a1a1a';
                      }}
                    />
                  </motion.div>
                </div>
              </motion.div>
              
              <h3 className="text-2xl font-semibold text-white mb-6">What I Do</h3>
              <div className="grid grid-cols-2 gap-4">
                {skills.map((skill, index) => (
                  <motion.div
                    key={skill}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={{ scale: 1.05, x: 5 }}
                    className="glass p-4 rounded-lg cursor-pointer"
                  >
                    <p className="text-gray-300">{skill}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;

